import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { ChartContainer, ScatterPlot, ChartsXAxis, ChartsYAxis, ChartsTooltipContainer, ChartsReferenceLine, useItemTooltip, useDrawingArea, useYScale, useXScale } from "@mui/x-charts";
import { useLayoutEffect, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import useOtSignalData from "../hooks/useOtSignalData";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isFiniteNum(v) {
  return v != null && isFinite(v) && !isNaN(v);
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 1, mb: 0.3 }}>
    <Typography variant="caption" sx={{ color: "#33c2c2", minWidth: 90, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ color: "#eee", wordBreak: "break-all" }}>
      {String(value ?? "—")}
    </Typography>
  </Box>
);

const TooltipContent = ({ row, periodStats }) => {
  const isAnomaly = row.anomaly === true;
  const isNormal = row.anomaly === false;
  const val = Number(row.value);
  const outOfRange =
    periodStats &&
    isFiniteNum(val) &&
    ((periodStats.lower_fence != null && val < periodStats.lower_fence) ||
      (periodStats.upper_fence != null && val > periodStats.upper_fence));

  return (
    <Box sx={{ p: 1.5, minWidth: 220, maxWidth: 320, background: "#1e1e2e", border: "1px solid #333", borderRadius: 5 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          display: "block",
          mb: 0.5,
          color: outOfRange ? "#ff4444" : isAnomaly ? "#ffb422" : isNormal ? "#4cceac" : "#aaa",
        }}
      >
        {outOfRange ? "✖ Out of range" : isAnomaly ? "⚠ Anomaly" : isNormal ? "✓ Normal" : "N/D"}
      </Typography>
      <Divider sx={{ mb: 0.8, borderColor: "#444" }} />
      <InfoRow label="Timestamp" value={dayjs(row.timestamp).format("YYYY-MM-DD HH:mm:ss")} />
      <InfoRow label="Type" value={row.is_write ? "Write" : "Read"} />
      {row.src_ip && (
        <InfoRow label="Source" value={`${row.src_ip}${row.src_port ? `:${row.src_port}` : ""}`} />
      )}
      {row.dest_ip && (
        <InfoRow label="Destination" value={`${row.dest_ip}${row.dest_port ? `:${row.dest_port}` : ""}`} />
      )}
      {row.protocol && <InfoRow label="Protocol" value={row.protocol} />}
      {row.operation && <InfoRow label="Operation" value={row.operation} />}
      {row.address && <InfoRow label="Address" value={row.address} />}
      {row.data_type && <InfoRow label="Data type" value={row.data_type} />}
      <InfoRow label="Value" value={isFiniteNum(val) ? val : row.value} />
      {periodStats && (
        <>
          <Divider sx={{ my: 0.8, borderColor: "#444" }} />
          <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 0.3 }}>
            Period stats
          </Typography>
          {periodStats.min !== undefined && <InfoRow label="Min" value={periodStats.min} />}
          {periodStats.q1 !== undefined && <InfoRow label="Q1" value={periodStats.q1} />}
          {periodStats.median !== undefined && <InfoRow label="Median" value={periodStats.median} />}
          {periodStats.mean !== undefined && (
            <InfoRow label="Mean" value={Number(periodStats.mean).toFixed(2)} />
          )}
          {periodStats.q3 !== undefined && <InfoRow label="Q3" value={periodStats.q3} />}
          {periodStats.max !== undefined && <InfoRow label="Max" value={periodStats.max} />}
          {periodStats.std_dev !== undefined && (
            <InfoRow label="Std dev" value={Number(periodStats.std_dev).toFixed(4)} />
          )}
        </>
      )}
    </Box>
  );
};

const ChartItemTooltip = ({ rowsById, periodStats }) => {
  const tooltip = useItemTooltip();
  if (!tooltip) return null;
  const { identifier } = tooltip;
  const key = `${identifier.seriesId}::${identifier.dataIndex}`;
  const row = rowsById[key];
  return (
    <ChartsTooltipContainer trigger="item">
      {row && <TooltipContent row={row} periodStats={periodStats} />}
    </ChartsTooltipContainer>
  );
};

// ─── IQR band ────────────────────────────────────────────────────────────────

const IqrBand = ({ q1, q3 }) => {
  const { left, width } = useDrawingArea();
  const yScale = useYScale();
  if (q1 == null || q3 == null || !yScale) return null;
  const top = yScale(Math.max(q1, q3));
  const bottom = yScale(Math.min(q1, q3));
  if (top == null || bottom == null || isNaN(top) || isNaN(bottom)) return null;
  return (
    <rect
      x={left}
      y={top}
      width={width}
      height={Math.max(0, bottom - top)}
      fill="#7b5fe0"
      opacity={0.22}
    />
  );
};

// ─── Trend line ───────────────────────────────────────────────────────────────

const TrendLine = ({ signalRows }) => {
  const xScale = useXScale();
  const yScale = useYScale();
  if (!xScale || !yScale || signalRows.length < 2) return null;

  const sorted = [...signalRows]
    .sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf());

  const pts = sorted
    .map((r) => ({
      x: xScale(dayjs(r.timestamp).valueOf()),
      y: yScale(Number(r.value)),
    }))
    .filter((p) => p.x != null && p.y != null && !isNaN(p.x) && !isNaN(p.y));

  if (pts.length < 2) return null;

  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  return <path d={d} stroke="#8899aa" strokeWidth={2} fill="none" opacity={0.5} />;
};

// ─── Scatter series config ────────────────────────────────────────────────────

const SERIES_META = [
  { id: "write_normal", label: "Write – Normal", color: "#6c8ef7", isWrite: true, isAnomaly: false, size: 3 },
  { id: "write_anomaly", label: "Write – Anomaly", color: "#ffb422", isWrite: true, isAnomaly: true, size: 5 },
  { id: "read_normal", label: "Read – Normal", color: "#4cceac", isWrite: false, isAnomaly: false, size: 3 },
  { id: "read_anomaly", label: "Read – Anomaly", color: "#ff6b35", isWrite: false, isAnomaly: true, size: 5 },
];

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Self-contained chart for a single OT signal.
 * Fetches its own data via /api/ot-events/ot-signal-data — no rows prop needed.
 *
 * @param {Array}    timeFilter     - [isoFrom, isoTo] — updated by OtDataGrid on filter change
 * @param {string}   selectedDevice - device ip, controlled by the parent (OtDeviceSignalSelector)
 * @param {string}   selectedSignal - signal full_address, controlled by the parent
 * @param {string}   signalLabel    - display name for selectedSignal (y-axis label / messages)
 * @param {Array}    filterModel    - active table filter model (propagated from OtDataGrid)
 * @param {string}   mode           - "r" | "w" | null
 * @param {Function} onDataChange   - callback(rows, stats, configRange) — rows are passed back
 *                                   for the grid (all matching records, value already
 *                                   signal-specific); stats/configRange are the same period-wide
 *                                   objects used for the IQR band/fences/config lines, so the
 *                                   grid's drawer can show them too
 * @param {Function} onPointClick   - callback(row) — fired when a scatter dot is clicked, with
 *                                   the same row shape passed to onDataChange
 * @param {number}   height         - chart height in px (default 340)
 */
const OtTimeSeriesChart = ({
  selectedDevice = null,
  selectedSignal = null,
  signalLabel = null,
  timeFilter = null,
  filterModel = [],
  mode = null,
  onDataChange = null,
  onPointClick = null,
  height = 340,
}) => {
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  // ── Responsive width ──
  // The resize callback is deferred to the next frame (rAF) — updating state synchronously
  // inside a ResizeObserver callback can itself change the observed element's size within the
  // same cycle (e.g. when this card's layout shifts as the Statistics/Chart tabs toggle
  // display), which triggers the browser's "ResizeObserver loop completed with undelivered
  // notifications" warning. Harmless, but CRA's dev overlay treats it as a blocking error.
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    setChartWidth(containerRef.current.getBoundingClientRect().width);
    let rafId = null;
    const ro = new ResizeObserver(([entry]) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setChartWidth(entry.contentRect.width));
    });
    ro.observe(containerRef.current);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  // ── Fetch rows + stats for the selected signal ──
  // Backend handles both composite (normalized.points[]) and single (normalized.full_address).
  // Returned rows already have `value` = signal-specific point value.
  // Stats are computed over ALL records in the period (not just the 3000-row chart limit).
  const dataPayload = useMemo(() => {
    if (!timeFilter || !selectedSignal) return null;
    return {
      timeFilter,
      full_address: selectedSignal,
      ...(filterModel?.length && { filter: filterModel }),
      ...(mode && { mode }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(timeFilter), selectedSignal, JSON.stringify(filterModel), mode]);

  const { rows: allSignalRows, stats, configRange, loading: dataLoading } = useOtSignalData(dataPayload);

  // Chart-plottable rows: those with a finite numeric value and a timestamp
  const signalRows = useMemo(
    () => allSignalRows.filter((r) => r.timestamp && isFiniteNum(Number(r.value))),
    [allSignalRows],
  );

  // ── Enrich all rows with outOfRange flag (for grid anomaly column) ──
  const enrichedRows = useMemo(() => {
    const hasConfig = configRange && (configRange.min != null || configRange.max != null);
    const lf = stats?.lower_fence;
    const uf = stats?.upper_fence;
    return allSignalRows.map((r) => {
      const v = Number(r.value);
      if (!isFiniteNum(v)) return { ...r, outOfRange: null };
      if (hasConfig) {
        const oor =
          (configRange.min != null && v < configRange.min) ||
          (configRange.max != null && v > configRange.max);
        return { ...r, outOfRange: oor };
      }
      if (stats && isFiniteNum(lf) && isFiniteNum(uf)) {
        return { ...r, outOfRange: v < lf || v > uf };
      }
      return { ...r, outOfRange: null };
    });
  }, [allSignalRows, stats, configRange]);

  // ── Pass ALL matching rows back to parent (for grid display) ──
  useEffect(() => {
    onDataChange?.(enrichedRows, stats, configRange);
  }, [enrichedRows, stats, configRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Y-axis domain ──
  const yDomain = useMemo(() => {
    const candidates = [];
    if (stats) {
      [stats.lower_fence, stats.min, stats.q1, stats.q3, stats.max, stats.upper_fence]
        .forEach((v) => { if (isFiniteNum(v)) candidates.push(v); });
    }
    if (configRange) {
      if (isFiniteNum(configRange.min)) candidates.push(configRange.min);
      if (isFiniteNum(configRange.max)) candidates.push(configRange.max);
    }
    signalRows.forEach((r) => { if (isFiniteNum(Number(r.value))) candidates.push(Number(r.value)); });
    if (!candidates.length) return {};
    const lo = Math.min(...candidates);
    const hi = Math.max(...candidates);
    const pad = (hi - lo) * 0.06 || 1;
    return { min: lo - pad, max: hi + pad };
  }, [stats, signalRows, configRange]);

  // ── Categorise: config-out-of-range (hard limits) > statistical out-of-range > rest ──
  // Config range takes priority: if configured min/max exist, use them as the anomaly boundary.
  // Fall back to IQR fences only when no config range is defined.
  const categorised = useMemo(() => {
    const hasConfig = configRange && (configRange.min != null || configRange.max != null);
    const lf = stats?.lower_fence;
    const uf = stats?.upper_fence;
    const configOutOfRange = [];
    const statOutOfRange = [];
    const rest = [];
    for (const r of signalRows) {
      const v = Number(r.value);
      if (hasConfig) {
        const belowMin = configRange.min != null && v < configRange.min;
        const aboveMax = configRange.max != null && v > configRange.max;
        if (belowMin || aboveMax) { configOutOfRange.push(r); continue; }
      }
      if (!hasConfig && stats && isFiniteNum(lf) && isFiniteNum(uf) && (v < lf || v > uf)) {
        statOutOfRange.push(r);
      } else {
        rest.push(r);
      }
    }
    return { configOutOfRange, outOfRange: statOutOfRange, rest };
  }, [signalRows, stats, configRange]);

  // ── Build scatter series + tooltip lookup ──
  const { series, rowsById } = useMemo(() => {
    const byId = {};
    const built = [];

    for (const meta of SERIES_META) {
      const filtered = categorised.rest.filter((r) => {
        const isW = r.is_write === true;
        const isA = r.anomaly === true;
        return isW === meta.isWrite && isA === meta.isAnomaly;
      });
      if (!filtered.length) continue;
      const data = filtered.map((r) => ({
        x: dayjs(r.timestamp).valueOf(),
        y: Number(r.value),
        id: r.id,
      }));
      data.forEach((pt, idx) => {
        byId[`${meta.id}::${idx}`] = filtered.find((r) => r.id === pt.id);
      });
      built.push({ id: meta.id, type: "scatter", label: meta.label, data, color: meta.color, markerSize: meta.size });
    }

    if (categorised.outOfRange.length) {
      const data = categorised.outOfRange.map((r) => ({
        x: dayjs(r.timestamp).valueOf(),
        y: Number(r.value),
        id: r.id,
      }));
      data.forEach((pt, idx) => {
        byId[`out_of_range::${idx}`] = categorised.outOfRange.find((r) => r.id === pt.id);
      });
      built.push({ id: "out_of_range", type: "scatter", label: "Stat. out of range", data, color: "#ff1744", markerSize: 4 });
    }

    if (categorised.configOutOfRange.length) {
      const data = categorised.configOutOfRange.map((r) => ({
        x: dayjs(r.timestamp).valueOf(),
        y: Number(r.value),
        id: r.id,
      }));
      data.forEach((pt, idx) => {
        byId[`config_anomaly::${idx}`] = categorised.configOutOfRange.find((r) => r.id === pt.id);
      });
      built.push({ id: "config_anomaly", type: "scatter", label: "Config anomaly", data, color: "#ff6600", markerSize: 4 });
    }

    return { series: built, rowsById: byId };
  }, [categorised]);

  const isLoading = dataLoading;
  const hasData = series.length > 0;
  const anomalyCount = signalRows.filter((r) => r.anomaly === true).length;
  const outOfRangeCount = categorised.outOfRange.length + categorised.configOutOfRange.length;

  return (
    <Box ref={containerRef} sx={{ width: "100%", mb: 3, p: 2.5, borderRadius: 2, border: "1px solid rgba(69,144,149,0.15)", backgroundColor: "rgba(20,20,20,0.8)" }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", mb: 2 }}>
        Value Trend vs Statistical Reference
      </Typography>

      {/* ── Color legend ── */}
      {!isLoading && hasData && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", mb: 1, pl: 0.5 }}>
          {[
            { color: "#6c8ef7", label: "Write – Normal", dot: true },
            { color: "#ffb422", label: "Write – Anomaly", dot: true },
            { color: "#4cceac", label: "Read – Normal", dot: true },
            { color: "#ff6b35", label: "Read – Anomaly", dot: true },
            ...(categorised.outOfRange.length ? [{ color: "#ff1744", label: "Stat. out of range", dot: true, size: 10 }] : []),
            ...(categorised.configOutOfRange.length ? [{ color: "#ff6600", label: "Config anomaly", dot: true, size: 11 }] : []),
            ...(stats ? [
              { color: "#7b5fe0", label: "IQR band (Q1–Q3)", band: true },
              { color: "#33c2c2", label: "Median", line: true },
              { color: "#aaaaff", label: "Mean", dash: true },
              { color: "#ff4444", label: "Fences", dash: true, thin: true },
            ] : []),
            ...(configRange ? [{ color: "#b89400", label: "Config limit", line: true }] : []),
          ].map(({ color, label, dot, band, line, dash, thin, size }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              {dot && <Box sx={{ width: size ?? 8, height: size ?? 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />}
              {band && <Box sx={{ width: 14, height: 8, bgcolor: color, opacity: 0.35, borderRadius: 0.5, flexShrink: 0 }} />}
              {line && <Box sx={{ width: 16, height: 2, bgcolor: color, flexShrink: 0 }} />}
              {dash && <Box sx={{ width: 16, height: 2, background: `repeating-linear-gradient(90deg,${color} 0,${color} 3px,transparent 3px,transparent 6px)`, flexShrink: 0, opacity: thin ? 0.8 : 1 }} />}
              <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Chart area ── */}
      {isLoading ? (
        <Skeleton variant="rounded" width="100%" height={height} />
      ) : !selectedSignal ? (
        <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid", borderColor: "divider", borderRadius: 1, color: "text.secondary" }}>
          <Typography variant="body2">Select a signal to view its trend</Typography>
        </Box>
      ) : !hasData ? (
        <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid", borderColor: "divider", borderRadius: 1, color: "text.secondary" }}>
          <Typography variant="body2">
            No data for "{signalLabel ?? selectedSignal}" in the selected range
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height }}>
          {chartWidth > 0 && (
            <ChartContainer
              width={chartWidth}
              height={height}
              series={series}
              xAxis={[{ valueFormatter: (v) => dayjs(v).format("HH:mm DD/MM"), label: "Time" }]}
              yAxis={[{ label: signalLabel ?? "Value", ...yDomain }]}
            >
              {stats && isFiniteNum(stats.q1) && isFiniteNum(stats.q3) && (
                <IqrBand q1={stats.q1} q3={stats.q3} />
              )}

              <TrendLine signalRows={signalRows} />

              <ScatterPlot
                onItemClick={
                  onPointClick &&
                  ((event, itemData) => {
                    const row = rowsById[`${itemData.seriesId}::${itemData.dataIndex}`];
                    if (row) onPointClick(row);
                  })
                }
              />
              <ChartsXAxis />
              <ChartsYAxis />

              {stats && isFiniteNum(stats.min) && (
                <ChartsReferenceLine y={stats.min} lineStyle={{ stroke: "#6e6e7e", strokeWidth: 1, strokeDasharray: "2 4" }} labelStyle={{ fill: "#aaa", fontSize: 10 }} label="Min" labelAlign="start" />
              )}
              {stats && isFiniteNum(stats.max) && (
                <ChartsReferenceLine y={stats.max} lineStyle={{ stroke: "#6e6e7e", strokeWidth: 1, strokeDasharray: "2 4" }} labelStyle={{ fill: "#aaa", fontSize: 10 }} label="Max" labelAlign="start" />
              )}
              {stats && isFiniteNum(stats.median) && (
                <ChartsReferenceLine y={stats.median} lineStyle={{ stroke: "#33c2c2", strokeWidth: 1.5 }} labelStyle={{ fill: "#33c2c2", fontSize: 11 }} label="Median" labelAlign="end" />
              )}
              {stats && isFiniteNum(stats.mean) && (
                <ChartsReferenceLine y={stats.mean} lineStyle={{ stroke: "#aaaaff", strokeWidth: 1, strokeDasharray: "4 4" }} labelStyle={{ fill: "#aaaaff", fontSize: 11 }} label="Mean" labelAlign="end" />
              )}
              {stats && isFiniteNum(stats.lower_fence) && (
                <ChartsReferenceLine y={stats.lower_fence} lineStyle={{ stroke: "#ff4444", strokeWidth: 1, strokeDasharray: "3 3" }} labelStyle={{ fill: "#ff4444", fontSize: 10 }} label="Lower fence" labelAlign="start" />
              )}
              {stats && isFiniteNum(stats.upper_fence) && (
                <ChartsReferenceLine y={stats.upper_fence} lineStyle={{ stroke: "#ff4444", strokeWidth: 1, strokeDasharray: "3 3" }} labelStyle={{ fill: "#ff4444", fontSize: 10 }} label="Upper fence" labelAlign="start" />
              )}
              {configRange && isFiniteNum(configRange.min) && (
                <ChartsReferenceLine y={configRange.min} lineStyle={{ stroke: "#b89400", strokeWidth: 1.5 }} labelStyle={{ fill: "#b89400", fontSize: 11 }} label="Config Min" labelAlign="end" />
              )}
              {configRange && isFiniteNum(configRange.max) && (
                <ChartsReferenceLine y={configRange.max} lineStyle={{ stroke: "#b89400", strokeWidth: 1.5 }} labelStyle={{ fill: "#b89400", fontSize: 11 }} label="Config Max" labelAlign="end" />
              )}

              <ChartItemTooltip rowsById={rowsById} periodStats={stats} />
            </ChartContainer>
          )}
        </Box>
      )}

      {/* ── Summary — below the chart, where the time filter used to sit ── */}
      {!isLoading && selectedSignal && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          {outOfRangeCount > 0 && (
            <Chip
              label={`${outOfRangeCount} out of range`}
              size="small"
              sx={{ bgcolor: "#ff174422", color: "#ff1744", fontWeight: 700, fontSize: 11 }}
            />
          )}
          {anomalyCount > 0 && (
            <Chip
              label={`${anomalyCount} anomal${anomalyCount === 1 ? "y" : "ies"}`}
              size="small"
              sx={{ bgcolor: "#ffb42222", color: "#ffb422", fontWeight: 700, fontSize: 11 }}
            />
          )}
          <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }}>
            {stats
              ? `Computed stats · ${stats.count} samples`
              : "No data for stats in this period"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OtTimeSeriesChart;
